import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Circle, Loader2, PlayCircle } from "lucide-react";

export default function ModulePlayerPage() {
  const { slug } = useParams();
  const [f, setF] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [activeLessonId, setActiveLessonId] = useState(null);

  useEffect(() => {
    http.get(`/formations/${slug}`).then((r) => {
      setF(r.data);
      if (r.data.modules?.[0]?.lessons?.[0]) setActiveLessonId(r.data.modules[0].lessons[0].id);
    });
    http.get("/me/enrollments").then((r) => {
      const e = r.data.find((x) => x.formation.slug === slug);
      setEnrollment(e || null);
    });
  }, [slug]);

  const completed = useMemo(() => new Set(enrollment?.completed_lessons || []), [enrollment]);
  const activeLesson = useMemo(() => {
    for (const m of f?.modules || []) {
      const l = m.lessons.find((x) => x.id === activeLessonId);
      if (l) return { lesson: l, module: m };
    }
    return null;
  }, [f, activeLessonId]);

  const totalLessons = f?.modules?.reduce((s, m) => s + m.lessons.length, 0) || 0;
  const doneCount = completed.size;
  const progress = totalLessons ? Math.round((doneCount / totalLessons) * 100) : 0;

  const toggleComplete = async () => {
    if (!activeLesson) return;
    const key = `${f.slug}::${activeLesson.module.id}::${activeLesson.lesson.id}`;
    const isDone = completed.has(key);
    try {
      await http.post("/me/progress", { formation_id: f.slug, module_id: `${activeLesson.module.id}::${activeLesson.lesson.id}`, completed: !isDone });
      const newSet = new Set(completed);
      if (isDone) newSet.delete(key); else newSet.add(key);
      setEnrollment({ ...enrollment, completed_lessons: [...newSet], progress: totalLessons ? Math.round((newSet.size / totalLessons) * 100) : 0 });
      toast.success(isDone ? "Marquée à faire" : "Leçon terminée");
    } catch { toast.error("Impossible de mettre à jour la progression"); }
  };

  if (!f) return <div className="min-h-[50vh] grid place-items-center"><Loader2 className="animate-spin text-brand-500" /></div>;

  return (
    <div className="grid lg:grid-cols-[1fr_360px] min-h-[calc(100vh-4rem)]">
      <div className="bg-slate-950 text-white p-6 lg:p-10">
        <Link to="/espace" className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-white"><ArrowLeft size={14} /> Retour à mon espace</Link>
        <div className="mt-6 max-w-4xl">
          <div className="text-xs uppercase tracking-widest text-yellow-400">{activeLesson?.module.title}</div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold mt-2">{activeLesson?.lesson.title || "Sélectionnez une leçon"}</h1>
          <div className="mt-6 aspect-video rounded-2xl overflow-hidden bg-black" data-testid="video-player-module">
            {activeLesson?.lesson.video_url ? (
              <iframe title={activeLesson.lesson.title} src={activeLesson.lesson.video_url} allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen className="w-full h-full" />
            ) : (
              <div className="w-full h-full grid place-items-center text-slate-500">Aucune vidéo</div>
            )}
          </div>
          <div className="mt-6 flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-400">Progression totale</div>
              <div className="font-display text-3xl font-bold text-yellow-400 mt-1">{progress}%</div>
            </div>
            <button data-testid="mark-complete" onClick={toggleComplete} className="btn-primary">
              {activeLesson && completed.has(`${f.slug}::${activeLesson.module.id}::${activeLesson.lesson.id}`)
                ? <>Marquer à faire</>
                : <>Marquer comme terminée <CheckCircle2 size={16} strokeWidth={2} /></>}
            </button>
          </div>
        </div>
      </div>

      <aside className="border-l border-slate-200 bg-white overflow-y-auto">
        <div className="p-6 border-b border-slate-100">
          <div className="eyebrow">Formation</div>
          <h2 className="font-display font-bold text-slate-900 mt-1">{f.title}</h2>
        </div>
        <div className="p-2">
          {f.modules.map((m, i) => (
            <div key={m.id} className="mb-3">
              <div className="px-4 py-2 text-xs uppercase tracking-widest font-semibold text-slate-500">Module {i + 1} · {m.title}</div>
              <ul>
                {m.lessons.map((l) => {
                  const key = `${f.slug}::${m.id}::${l.id}`;
                  const done = completed.has(key);
                  const active = activeLessonId === l.id;
                  return (
                    <li key={l.id}>
                      <button onClick={() => setActiveLessonId(l.id)} data-testid={`lesson-${l.id}`} className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left transition-colors ${active ? "bg-brand-50 text-brand-900" : "hover:bg-slate-50 text-slate-800"}`}>
                        {done ? <CheckCircle2 size={16} className="text-emerald-500" strokeWidth={2} /> : <Circle size={16} className="text-slate-400" strokeWidth={1.5} />}
                        <PlayCircle size={14} strokeWidth={1.5} className="text-slate-400" />
                        <span className="flex-1">{l.title}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      </aside>
    </div>
  );
}
